import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiArrowLeft, HiCake, HiClock, HiUsers, HiHeart, HiPlus, HiGift } from 'react-icons/hi';
import { Link, useNavigate } from 'react-router-dom';
import LiquidEther from './LiquidEther';
import { supabase } from '../supabaseClient'; // Import Supabase client

const BirthdayPage = () => {
  const [friends, setFriends] = useState([]); // State for all friends from Supabase
  const [nextBirthday, setNextBirthday] = useState(null);
  const [todaysBirthdays, setTodaysBirthdays] = useState([]);
  const [timeLeft, setTimeLeft] = useState({});
  const [cards, setCards] = useState([]);
  const [creatorId, setCreatorId] = useState(null); // Add creator ID state
  const navigate = useNavigate(); // Add navigate hook

  // Get creator ID from localStorage on component mount
  useEffect(() => {
    const id = localStorage.getItem('birthdayCardCreatorId');
    setCreatorId(id);
  }, []);

  // Fetch birthdays from Supabase
  useEffect(() => {
    const fetchBirthdays = async () => {
      try {
        const { data, error } = await supabase
          .from('birthdays')
          .select('*')
          .order('name');
        
        if (error) {
          console.error('Error fetching birthdays:', error);
        } else {
          setFriends(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
      }
    };

    fetchBirthdays();
  }, []);

  // Fetch birthday cards and cleanup expired ones
  useEffect(() => {
    const fetchCards = async () => {
      try {
        // First, clean up expired cards
        await cleanupExpiredCards();
        
        // Then fetch the remaining cards
        let query = supabase
          .from('birthday_cards')
          .select('*')
          .order('created_at', { ascending: false });
        
        // If user is not logged in or creator ID is not set, only fetch public cards
        if (!creatorId) {
          query = query.or('is_private.eq.false,is_private.is.null');
        } else {
          // If user is logged in, fetch public cards and their private cards
          query = query.or(`is_private.eq.false,is_private.is.null,creator_id.eq.${creatorId}`);
        }
        
        const { data, error } = await query;
        
        if (error) {
          console.error('Error fetching birthday cards:', error);
        } else {
          setCards(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
      }
    };

    fetchCards();
  }, [creatorId]); // Add creatorId as dependency

  // Add state for card expiration timers
  const [cardTimers, setCardTimers] = useState({});

  // Update card timers
  useEffect(() => {
    if (cards.length === 0) return;

    const updateTimers = () => {
      const timers = {};
      cards.forEach(card => {
        if (!card.is_permanent && card.expires_at) {
          const now = new Date();
          const expiresAt = new Date(card.expires_at);
          const diff = expiresAt - now;
          
          if (diff > 0) {
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            timers[card.id] = { hours, minutes };
          } else {
            timers[card.id] = { expired: true };
          }
        }
      });
      setCardTimers(timers);
    };

    updateTimers();
    const interval = setInterval(updateTimers, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [cards]);

  // Function to clean up expired cards
  const cleanupExpiredCards = async () => {
    try {
      // Get current time
      const now = new Date().toISOString();
      
      // Find expired cards (not permanent and past expiration date)
      const { data: expiredCards, error: fetchError } = await supabase
        .from('birthday_cards')
        .select('id, card_image_url, card_video_url, card_audio_url')
        .lt('expires_at', now)
        .eq('is_permanent', false);

      if (fetchError) {
        console.error('Error fetching expired cards:', fetchError);
        return;
      }

      if (expiredCards.length === 0) {
        return; // No expired cards to clean up
      }

      console.log(`Found ${expiredCards.length} expired card(s) to clean up`);
      
      // Process each expired card
      for (const card of expiredCards) {
        console.log(`Processing card ${card.id}...`);
        
        // Extract file names from URLs and delete them from storage
        const mediaUrls = [
          card.card_image_url,
          card.card_video_url,
          card.card_audio_url
        ].filter(url => url); // Filter out null/undefined URLs
        
        // Delete files from storage
        for (const url of mediaUrls) {
          try {
            // Extract file path from URL
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            const bucketIndex = pathParts.indexOf('birthday-cards');
            
            if (bucketIndex !== -1 && bucketIndex < pathParts.length - 1) {
              // Reconstruct the file path within the bucket
              const filePath = pathParts.slice(bucketIndex + 1).join('/');
              
              console.log(`  Deleting file: ${filePath}`);
              
              const { error: deleteError } = await supabase
                .storage
                .from('birthday-cards')
                .remove([filePath]);
              
              if (deleteError) {
                console.warn(`  Warning: Could not delete file ${filePath}:`, deleteError.message);
              } else {
                console.log(`  Deleted file: ${filePath}`);
              }
            }
          } catch (urlError) {
            console.warn(`  Warning: Could not parse URL ${url}:`, urlError.message);
          }
        }
        
        // Delete the card record from the database
        const { error: deleteError } = await supabase
          .from('birthday_cards')
          .delete()
          .eq('id', card.id);
        
        if (deleteError) {
          console.error(`Error deleting card ${card.id}:`, deleteError);
        } else {
          console.log(`  Deleted card record: ${card.id}`);
        }
      }
      
      console.log(`Cleanup complete!`);
      
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  };

  // Update birthday info when friends data changes
  useEffect(() => {
    const updateBirthdayInfo = () => {
      if (friends.length === 0) return;

      const next = getNextBirthday(friends);
      const today = getTodaysBirthdays(friends);
      
      setNextBirthday(next);
      setTodaysBirthdays(today);
    };

    updateBirthdayInfo();

    // Update every minute
    const interval = setInterval(updateBirthdayInfo, 60000);

    return () => clearInterval(interval);
  }, [friends]);

  useEffect(() => {
    if (!nextBirthday) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const target = nextBirthday.nextDate;
      const difference = target - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [nextBirthday]);

  // Helper function to get next birthday
  const getNextBirthday = (friendsList) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    
    let nextBirthday = null;
    let minDaysUntil = Infinity;
    
    friendsList.forEach(friend => {
      const birthdayDate = new Date(friend.birthday);
      const thisYearBirthday = new Date(currentYear, birthdayDate.getMonth(), birthdayDate.getDate());
      
      // If birthday has passed this year, check next year
      if (thisYearBirthday < today) {
        thisYearBirthday.setFullYear(currentYear + 1);
      }
      
      const daysUntil = Math.ceil((thisYearBirthday - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntil < minDaysUntil) {
        minDaysUntil = daysUntil;
        nextBirthday = {
          ...friend,
          nextDate: thisYearBirthday,
          daysUntil
        };
      }
    });
    
    return nextBirthday;
  };

  // Helper function to check if someone has a birthday today
  const getTodaysBirthdays = (friendsList) => {
    const today = new Date();
    const month = today.getMonth() + 1;
    const date = today.getDate();
    
    return friendsList.filter(friend => {
      const birthdayDate = new Date(friend.birthday);
      return birthdayDate.getMonth() + 1 === month && birthdayDate.getDate() === date;
    });
  };

  // Helper function to format countdown
  const formatCountdown = (daysUntil) => {
    if (daysUntil === 0) return "Today! 🎉";
    if (daysUntil === 1) return "Tomorrow! 🎊";
    
    const weeks = Math.floor(daysUntil / 7);
    const remainingDays = daysUntil % 7;
    
    if (weeks > 0) {
      return `${weeks} week${weeks > 1 ? 's' : ''} and ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
    }
    
    return `${daysUntil} day${daysUntil > 1 ? 's' : ''}`;
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (!nextBirthday) {
    return (
      <div className="min-h-screen px-6 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎂</div>
          <h1 className="text-2xl font-space-grotesk font-bold text-white mb-2">
            Loading Birthday Info...
          </h1>
          <p className="text-gray-400">Preparing something special for you!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 relative overflow-hidden">
      {/* LiquidEther Background - Full Screen - Hidden on mobile */}
      <div className="fixed inset-0 z-0 bg-black hidden sm:block" style={{ pointerEvents: 'none' }}>
        <LiquidEther
          colors={['#ff6b9d', '#ff9ffc', '#c44569']}
          mouseForce={20}
          cursorSize={100}
          isViscous={false}
          viscous={30}
          iterationsViscous={32}
          iterationsPoisson={32}
          resolution={0.5}
          isBounce={false}
          autoDemo={true}
          autoSpeed={0.8}
          autoIntensity={3.5}
          takeoverDuration={0.3}
          autoResumeDelay={1000}
          autoRampDuration={0.8}
          style={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0, pointerEvents: 'none' }}
        />
      </div>
      
      {/* Static background image for mobile */}
      <div 
        className="fixed inset-0 z-0 bg-cover bg-center sm:hidden"
        style={{ 
          backgroundImage: "url('/images/bg.jpg')",
          pointerEvents: 'none' 
        }}
      />
      
      {/* Floating Birthday Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-20">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-2xl opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          >
            {['🎂', '🎉', '🎊', '🎈', '🎁', '✨', '🌟', '💫'][Math.floor(Math.random() * 8)]}
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto pt-20 pointer-events-none">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link 
            to="/" 
            className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors group pointer-events-auto"
          >
            <HiArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center mb-16"
        >
          <motion.div variants={itemVariants} className="mb-6">
            <div className="text-8xl mb-4">🎂</div>
            <h1 className="text-display-2 font-space-grotesk font-bold gradient-text-green mb-4">
              Birthday Central
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Celebrating the amazing people in our lives with countdowns, messages, and lots of love! 💝
            </p>
          </motion.div>
        </motion.div>

        {/* Birthday Card Creator Button */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center mb-8"
        >
          <motion.div variants={itemVariants}>
            <button
              onClick={() => navigate('/create-birthday-card')}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-medium py-3 px-6 rounded-lg hover:from-purple-700 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 pointer-events-auto"
            >
              <HiPlus className="w-5 h-5" />
              <span>Create Birthday Card</span>
              <HiGift className="w-5 h-5" />
            </button>
          </motion.div>
        </motion.div>

        {/* Today's Birthdays */}
        {todaysBirthdays.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mb-16"
          >
            <motion.div variants={itemVariants} className="text-center mb-8">
              <div className="inline-flex items-center space-x-2 glass-morphism px-6 py-3 rounded-full mb-4">
                <HiCake className="text-accent-green" />
                <span className="font-jetbrains text-accent-green tracking-wider text-sm">
                  TODAY'S BIRTHDAYS
                </span>
              </div>
              <h2 className="text-display-3 font-space-grotesk font-bold text-white">
                🎉 Special Day! 🎉
              </h2>
            </motion.div>

            <div className="flex justify-center">
              {todaysBirthdays.map((birthday) => (
                <motion.div
                  key={birthday.id}
                  variants={itemVariants}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="glass-morphism-strong rounded-2xl p-8 text-center border-2 border-accent-green/30 flex flex-col items-center justify-center min-h-[400px] max-w-md pointer-events-auto"
                >
                  <div className="text-6xl mb-4">{birthday.avatar}</div>
                  <h3 className="text-2xl font-space-grotesk font-bold text-white mb-4 text-center">
                    {birthday.name}
                  </h3>
                  <div className="mb-6 text-center">
                    <div className="text-4xl mb-2">🎂</div>
                    <p className="text-accent-green font-medium">HAPPY BIRTHDAY!</p>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed text-center">
                    {birthday.message}
                  </p>
                  <div className="mt-6 flex justify-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent-green to-accent-blue rounded-full flex items-center justify-center animate-pulse">
                      <HiHeart className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Next Birthday Countdown */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 glass-morphism px-6 py-3 rounded-full mb-4">
              <HiClock className="text-accent-blue" />
              <span className="font-jetbrains text-accent-blue tracking-wider text-sm">
                NEXT BIRTHDAY
              </span>
            </div>
            <h2 className="text-display-3 font-space-grotesk font-bold text-white mb-4">
              {nextBirthday.name}'s Special Day
            </h2>
            <p className="text-xl text-gray-300">
              {nextBirthday.daysUntil === 0 
                ? "🎉 It's today! 🎉" 
                : nextBirthday.daysUntil === 1
                  ? "🎉 Coming up tomorrow! 🎉"
                  : `Coming up in ${formatCountdown(nextBirthday.daysUntil)}`
              }
            </p>
          </motion.div>

          {/* Countdown Timer */}
          {nextBirthday.daysUntil > 0 && (
            <motion.div
              variants={itemVariants}
              className="glass-morphism-strong rounded-2xl p-8 max-w-4xl mx-auto"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {Object.entries(timeLeft).map(([unit, value]) => (
                  <div key={unit} className="text-center">
                    <div className="text-4xl md:text-5xl font-space-grotesk font-bold text-accent-blue mb-2">
                      {value.toString().padStart(2, '0')}
                    </div>
                    <div className="text-sm text-gray-400 uppercase tracking-wider">
                      {unit}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 text-center">
                <div className="text-6xl mb-4">{nextBirthday.avatar}</div>
                <p className="text-gray-300 text-lg">
                  {nextBirthday.name} will be celebrating on{' '}
                  <span className="text-accent-green font-medium">
                    {nextBirthday.nextDate.toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* All Friends List */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 glass-morphism px-6 py-3 rounded-full mb-4">
              <HiUsers className="text-purple-400" />
              <span className="font-jetbrains text-purple-400 tracking-wider text-sm">
                ALL FRIENDS
              </span>
            </div>
            <h2 className="text-display-3 font-space-grotesk font-bold text-white">
              Birthday Calendar
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {friends.map((friend) => {
              const birthdayDate = new Date(friend.birthday);
              const thisYearBirthday = new Date(
                new Date().getFullYear(),
                birthdayDate.getMonth(),
                birthdayDate.getDate()
              );
              
              // If birthday has passed this year, check next year
              if (thisYearBirthday < new Date()) {
                thisYearBirthday.setFullYear(new Date().getFullYear() + 1);
              }
              
              const daysUntil = Math.ceil((thisYearBirthday - new Date()) / (1000 * 60 * 60 * 24));
              const isToday = daysUntil === 0;
              const isNext = daysUntil === nextBirthday?.daysUntil;

              return (
                <motion.div
                  key={friend.id}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02, y: -2 }}
                  className={`glass-morphism-strong rounded-2xl p-6 transition-all duration-200 pointer-events-auto ${
                    isToday 
                      ? 'border-2 border-accent-green/50 bg-accent-green/10' 
                      : isNext 
                        ? 'border-2 border-accent-blue/50 bg-accent-blue/10'
                        : 'border border-white/10'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="text-3xl">{friend.avatar}</div>
                    <div className="flex-1">
                      <h3 className="font-space-grotesk font-bold text-white mb-1">
                        {friend.name}
                      </h3>
                      <p className="text-sm text-gray-400 mb-2">
                        {birthdayDate.toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      <div className="flex items-center space-x-2">
                        {isToday ? (
                          <span className="text-accent-green text-sm font-medium flex items-center">
                            <HiCake className="w-4 h-4 mr-1" />
                            Today! 🎉
                          </span>
                        ) : (
                          <span className="text-gray-300 text-sm">
                            {daysUntil} day{daysUntil > 1 ? 's' : ''} away
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Created Birthday Cards */}
        {cards.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mb-16"
          >
            <motion.div variants={itemVariants} className="text-center mb-8">
              <div className="inline-flex items-center space-x-2 glass-morphism px-6 py-3 rounded-full mb-4">
                <HiGift className="text-pink-400" />
                <span className="font-jetbrains text-pink-400 tracking-wider text-sm">
                  CREATED BIRTHDAY CARDS
                </span>
              </div>
              <h2 className="text-display-3 font-space-grotesk font-bold text-white">
                Custom Birthday Cards
              </h2>
              {creatorId && (
                <p className="text-gray-400 text-sm mt-2">
                  Showing public cards and your private cards
                </p>
              )}
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cards.map((card) => (
                <motion.div
                  key={card.id}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02, y: -2 }}
                  className="glass-morphism-strong rounded-2xl p-6 transition-all duration-200 pointer-events-auto border border-white/10"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <HiGift className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-space-grotesk font-bold text-white truncate">
                        {card.title || 'Untitled Card'}
                      </h3>
                      <p className="text-sm text-gray-400">
                        For {card.recipient_name || 'Someone Special'}
                      </p>
                      {card.is_private && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-purple-900/50 text-purple-300 mt-1">
                          Private
                        </span>
                      )}
                    </div>
                  </div>
                
                  <div className="mb-4">
                    <p className="text-gray-300 text-sm line-clamp-2">
                      {card.message || 'No message'}
                    </p>
                  </div>
                
                  <div className="flex justify-between items-center text-xs text-gray-400">
                    <span>
                      {card.is_permanent ? 'Permanent' : (
                        card.expires_at ? (
                          cardTimers[card.id] ? (
                            cardTimers[card.id].expired ? (
                              'Expired'
                            ) : (
                              `Expires in ${cardTimers[card.id].hours}h ${cardTimers[card.id].minutes}m`
                            )
                          ) : (
                            'Loading...'
                          )
                        ) : (
                          'Expires in 24h'
                        )
                      )}
                    </span>
                    <span>{new Date(card.created_at).toLocaleDateString()}</span>
                  </div>
                
                  <div className="mt-4">
                    <a
                      href={`/birthday-card/${card.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full btn-view-card text-center text-sm"
                    >
                      View Card
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Message */}
        <motion.div
          variants={itemVariants}
          className="text-center py-8"
        >
          <div className="glass-morphism rounded-2xl p-8 max-w-2xl mx-auto">
            <div className="text-4xl mb-4">💝</div>
            <h3 className="text-xl font-space-grotesk font-bold text-white mb-2">
              Spread the Birthday Love!
            </h3>
            <p className="text-gray-300">
              Don't forget to send your friends a special birthday message and make their day extra special! 
              Every birthday is a new beginning and a reason to celebrate. 🎊
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BirthdayPage;